import {
	Grid,
	Checkbox,
	ListItemText,
	Card,
	CardHeader,
	CardContent,
	List,
	ListItem,
	ListItemIcon,
	Button,
	CircularProgress,
	Typography
} from '@mui/material';
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { generateClient } from "aws-amplify/api";
import { listContactFieldSuppressionLists } from "../../../graphql/queries";
import { fetchAllPages } from '../../../tools/graphqlPaging';
import { actionOneButtonStyle, actionTwoButtonStyle, cardStyle } from '../../../theme';

export default memo(function ContactFieldSuppressionLists(props) {
	const client = generateClient();

	const { formikProps, checkIndex, errors, touched } = props;

	// Get current list from Formik
	const lists =
		formikProps.values?.checks?.[checkIndex]?.contactFieldSuppressions?.contactFieldSuppressions ??
		[];

	const setFormikList = (value) => {
		formikProps.setFieldValue(
			`checks.${checkIndex}.contactFieldSuppressions.contactFieldSuppressions`,
			value
		);
	};

	const [loading, setLoading] = useState(false);
	const [availableLists, setAvailableLists] = useState([]);
	const rawListsMap = useRef({});

	const getData = useCallback(async () => {
		setLoading(true);

		const dynamoLists = await fetchAllPages({
			client,
			query: listContactFieldSuppressionLists,
			resultKey: 'listContactFieldSuppressionLists'
		});

		const updatedLists = dynamoLists.map(item =>
			Object.assign(item, { selected: false })
		);

		// Exclude lists already selected (JSON strings)
		setAvailableLists(
			updatedLists.filter(
				x => !lists.some(y => JSON.parse(y).id === x.id)
			)
		);

		rawListsMap.current = updatedLists.reduce(
			(acc, cur) => ({ ...acc, [cur.id]: cur }),
			{}
		);

		setLoading(false);
	}, [lists]);

	useEffect(() => {
		getData();
	}, []);

	const handleAdd = useCallback(() => {
		setAvailableLists(cur => {
			const selected = [];
			const unselected = [];

			for (const list of cur) {
				if (list.selected) {
					selected.push(
						JSON.stringify({
							id: list.id,
							field: list.contactField
						})
					);
					list.selected = false;
				} else unselected.push(list);
			}

			setFormikList([...lists, ...selected]);

			return unselected;
		});
	}, [lists]);

	const handleRemove = useCallback(() => {
		setAvailableLists(cur => {
			const removed = [];
			const kept = [];

			for (const listStr of lists) {
				const parsed = JSON.parse(listStr);

				if (rawListsMap.current[parsed.id].selected) {
					removed.push(rawListsMap.current[parsed.id]);
					rawListsMap.current[parsed.id].selected = false;
				} else {
					kept.push(listStr);
				}
			}

			setFormikList([...kept]);

			return [...cur, ...removed];
		});
	}, [lists]);

	const handleAvailableToggle = useCallback(index => {
		setAvailableLists(items => {
			items[index].selected = !items[index].selected;
			return [...items];
		});
	}, []);

	const handleSelectedToggle = useCallback(id => {
		rawListsMap.current[id].selected =
			!rawListsMap.current[id].selected;
		setAvailableLists(cur => [...cur]);
	}, []);

	const errorText =
		errors?.checks?.[checkIndex]?.contactFieldSuppressions ??
		formikProps.errors?.checks?.[checkIndex]?.contactFieldSuppressions;

	const touchedBlock =
		touched?.checks?.[checkIndex]?.contactFieldSuppressions ??
		formikProps.touched?.checks?.[checkIndex]?.contactFieldSuppressions;

	return (
		<Grid container spacing={2} direction="column">
			{errorText && touchedBlock && (
				<Grid item>
					<Typography variant="body1" color="error">
						{errorText}
					</Typography>
				</Grid>
			)}

			{loading && (
				<Grid item>
					<CircularProgress color="primary" />
				</Grid>
			)}

			{!loading && (
				<Grid item>
					<Grid container spacing={2} alignItems="center">
						{/* AVAILABLE LISTS */}
						<Grid item>
							<Card variant="outlined" sx={cardStyle}>
								<CardHeader title="Available Lists" />
								<CardContent>
									<List dense>
										{availableLists.map((list, index) => (
											<FastListItem
												key={index}
												id={list.id}
												name={list.name}
												selected={list.selected}
												onClick={() =>
													handleAvailableToggle(index)
												}
											/>
										))}
									</List>
								</CardContent>
							</Card>
						</Grid>

						{/* BUTTONS */}
						<Grid item>
							<Grid
								container
								spacing={1}
								direction="column"
								justifyContent="center"
							>
								<Grid item>
									<Button sx={actionTwoButtonStyle}>≫</Button>
								</Grid>
								<Grid item>
									<Button
										sx={actionOneButtonStyle}
										onClick={handleAdd}
									>
										&gt;
									</Button>
								</Grid>
								<Grid item>
									<Button
										sx={actionOneButtonStyle}
										onClick={handleRemove}
									>
										&lt;
									</Button>
								</Grid>
								<Grid item>
									<Button sx={actionTwoButtonStyle}>≪</Button>
								</Grid>
							</Grid>
						</Grid>

						{/* SELECTED LISTS */}
						<Grid item>
							<Card variant="outlined" sx={cardStyle}>
								<CardHeader title="Selected Lists" />
								<CardContent>
									<List dense>
										{Object.keys(rawListsMap.current)
											.length > 0 &&
											lists.map(listStr => {
												const parsed =
													JSON.parse(listStr);
												return (
													<FastListItem
														key={parsed.id}
														id={parsed.id}
														name={
															rawListsMap.current[
																parsed.id
															].name
														}
														selected={
															rawListsMap.current[
																parsed.id
															].selected
														}
														onClick={() =>
															handleSelectedToggle(
																parsed.id
															)
														}
													/>
												);
											})}
									</List>
								</CardContent>
							</Card>
						</Grid>
					</Grid>
				</Grid>
			)}
		</Grid>
	);
}, (prev, next) => {
	return (
		prev.formikProps.values?.checks?.[prev.checkIndex]?.contactFieldSuppressions?.contactFieldSuppressions ===
		next.formikProps.values?.checks?.[next.checkIndex]?.contactFieldSuppressions?.contactFieldSuppressions &&
		prev.errors === next.errors &&
		prev.touched === next.touched
	);
});

const FastListItem = memo(function FastListItem(props) {
	return (
		<ListItem onClick={props.onClick}>
			<ListItemIcon>
				<Checkbox color="primary" checked={props.selected ?? false} />
			</ListItemIcon>
			<ListItemText primary={props.name} />
		</ListItem>
	);
}, (prev, next) => prev.id === next.id && prev.selected === next.selected);
